import { useFormContext } from 'react-hook-form';
import { useRef, useState } from 'react';
import { useClickAway } from 'react-use';
import { noop } from 'lodash-es';
import FormLink from './components/FormLink';
import FormLinkDescription from './components/FormLinkDescription';
import { CaseLinkType } from '@/api/apis/case';

export interface FormLinkProps {
  index: number;
  formFieldName: string;
  handleDelete: () => void;
  handleSave?: (data: CaseLinkType) => void;
  disabled?: boolean;
  isEditing?: boolean;
  maxLength?: number;
  setEditingIndex?: (index: number | null) => void;
  isDescriptionVisible?: boolean;
  isDescriptionEnabled?: boolean;
}

export const FormLinkDescriptionElement = ({
  index,
  formFieldName,
  handleDelete,
  handleSave = noop,
  disabled,
  isEditing: isEditingProp,
  maxLength,
  setEditingIndex,
  isDescriptionVisible = true,
  isDescriptionEnabled = true,
}: FormLinkProps) => {
  const { control, formState, getValues, trigger } = useFormContext();
  const formFieldNameWithIndex = `${formFieldName}[${index}]`;
  const linkData = getValues(formFieldName)[index];

  const [localIsEditing, setLocalIsEditing] = useState(!!linkData?.isNew);
  const isEditing = typeof isEditingProp === 'boolean' ? isEditingProp : localIsEditing;

  const containerRef = useRef(null);

  const setEditing = (editingIndex: number | null) => {
    if (setEditingIndex) {
      setEditingIndex(editingIndex);
    } else {
      setLocalIsEditing(editingIndex !== null);
    }
  };

  useClickAway(containerRef, () => {
    if (!isEditing) return;

    const linkErrors = (formState?.errors?.[formFieldName] ?? []) as string[];
    const hasErrors = !!linkErrors[index];

    if (!hasErrors && linkData?.url) {
      handleSave(linkData);
      setEditing(null);
    } else {
      trigger(formFieldNameWithIndex);
    }
  });

  if (!linkData) {
    return null;
  }

  const shouldRenderDescription =
    isDescriptionEnabled && (isEditing || !!linkData.description?.length || isDescriptionVisible);

  return (
    // eslint-disable-next-line jsx-a11y/click-events-have-key-events
    <div
      tabIndex={0}
      role="button"
      className="flex min-w-0 w-full max-w-full flex-1 flex-col gap-2"
      ref={containerRef}
      onClick={() => {
        if (!disabled) {
          setEditing(index);
        }
      }}
    >
      <FormLink
        control={control}
        formFieldName={formFieldNameWithIndex}
        isEditing={isEditing}
        url={linkData.url}
        deleteLink={handleDelete}
        disabled={disabled}
      />

      {shouldRenderDescription ? (
        <FormLinkDescription
          control={control}
          formFieldName={formFieldNameWithIndex}
          disabled={disabled}
          maxLength={maxLength}
        />
      ) : null}
    </div>
  );
};
